import {
  apiErrorMessage,
  fetchQuestionnairePrivacy,
  saveQuestionnairePrivacy
} from '@api/taptotip/questionnaire';
import React, { useEffect, useState } from 'react';
import { CARD } from './Editor';

/**
 * 2026-10-07 — the guest form asks for a name and email, so it links to the
 * company's privacy policy. This is the company's one policy link, the same
 * one the ticketing booking page uses, so changing it here changes it there.
 */
const PrivacySettings = () => {
  const [saved, setSaved] = useState(null);
  const [value, setValue] = useState('');
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetchQuestionnairePrivacy()
      .then(result => {
        setSaved(result.privacyUrl || '');
        setValue(result.privacyUrl || '');
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, []);

  const save = () => {
    setStatus('saving');
    setError('');
    setDone(false);
    saveQuestionnairePrivacy(value.trim())
      .then(result => {
        setSaved(result.privacyUrl || '');
        setValue(result.privacyUrl || '');
        setStatus('ready');
        setDone(true);
      })
      .catch(err => {
        setError(apiErrorMessage(err));
        setStatus('ready');
      });
  };

  if (status === 'error') return null;
  const dirty = saved !== null && value.trim() !== saved;

  return (
    <section className={CARD} data-testid="privacy-settings">
      <h2 className="text-base font-black text-slate-900 m-0">Your privacy policy</h2>
      <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1 max-w-[68ch]">
        Guests can leave their name and email, so every questionnaire links to your privacy policy.
        Without a link, guests are told only that their answers go to you.
      </p>
      <div className="flex flex-col sm:flex-row gap-2 mt-4 max-w-2xl">
        <input
          type="url"
          inputMode="url"
          value={value}
          disabled={status === 'loading'}
          placeholder="https://yourcompany.com/privacy"
          aria-label="Privacy policy link"
          onChange={e => {
            setValue(e.target.value);
            setError('');
            setDone(false);
          }}
          className="flex-1 min-w-0 text-sm text-slate-900 placeholder-slate-400 border border-solid border-slate-300 rounded-xl px-3 py-2.5 focus:outline-none focus:border-indigo-500"
          data-testid="privacy-url"
        />
        <button
          type="button"
          disabled={!dirty || status !== 'ready'}
          onClick={save}
          className="bg-indigo-600 hover:bg-indigo-700 active:translate-y-px disabled:opacity-60 text-white text-sm font-bold border-0 rounded-xl px-4 py-2.5 cursor-pointer transition-colors"
          data-testid="privacy-save">
          {status === 'saving' ? 'Saving' : 'Save'}
        </button>
      </div>
      {error && (
        <p className="text-sm text-rose-700 m-0 mt-2" role="alert">
          {error}
        </p>
      )}
      {done && !error && (
        <p className="text-sm text-emerald-700 m-0 mt-2" role="status" data-testid="privacy-saved">
          {saved ? 'Saved. Guests now see this link.' : 'Removed. Guests no longer see a link.'}
        </p>
      )}
    </section>
  );
};

export default PrivacySettings;
